/**
 * Phase 7 hotfix round 7 (R7-#1, 2026-05-26) — modal alert при попытке ввести
 * duplicate contractNum (immediate feedback на onBlur).
 *
 * **Existing R5-#1 inline validation в BottomActions.handleSave** остаётся
 * как defense-in-depth — если user обходит onBlur (paste + immediate save без
 * blur, либо browser quirk), save-time check catches conflict.
 *
 * Mirror pattern UnavailableModal: overlay 50% bg-black/50, центральная
 * карточка bg-canvas radius 20, role=dialog + aria-modal + aria-labelledby.
 * Initial focus на OK кнопку. Esc + overlay click = onConfirm (informational
 * alert — единственная action = подтвердить + закрыть).
 *
 * Text verbatim per Aibek (visual verify round 12):
 *   «Укажите другой номер договора! Договор с таким номером уже сохранен»
 *
 * a11y: role=dialog, aria-modal, aria-labelledby. Initial focus на OK button.
 * Закрытие: overlay click, Escape, кнопка OK.
 */

import { useEffect, useRef } from 'react';

export interface ContractNumConflictDialogProps {
  isOpen: boolean;
  /**
   * Callback после подтверждения. SectionPreamble: clear contractNum field +
   * setConflictDialogOpen(false).
   */
  onConfirm: () => void;
}

export function ContractNumConflictDialog({ isOpen, onConfirm }: ContractNumConflictDialogProps) {
  const okButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onConfirm();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onConfirm]);

  useEffect(() => {
    if (isOpen) okButtonRef.current?.focus();
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      onClick={onConfirm}
      style={{ position: 'fixed', inset: 0, zIndex: 100, padding: 20 }}
      className="flex items-center justify-center bg-black/50"
      data-testid="contract-num-conflict-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="contract-num-conflict-title"
        className="w-full bg-canvas text-center"
        style={{
          maxWidth: 360,
          borderRadius: 20,
          padding: 24,
          boxShadow: '0 16px 64px rgba(0,0,0,.4)',
        }}
      >
        <h2
          id="contract-num-conflict-title"
          className="font-bold text-ink leading-snug"
          style={{ fontSize: 19, margin: '0 0 24px' }}
          data-testid="contract-num-conflict-title"
        >
          Укажите другой номер договора! Договор с таким номером уже сохранен
        </h2>
        <button
          ref={okButtonRef}
          type="button"
          onClick={onConfirm}
          aria-label="Понятно"
          data-testid="contract-num-conflict-ok"
          className={
            'w-full font-bold bg-cta text-ink ' +
            'min-h-tap-chip-lg rounded-2xl ' +
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:ring-offset-2 ' +
            'focus-visible:outline-none active:opacity-90 transition-opacity touch-manipulation'
          }
          style={{ fontSize: 19 }}
        >
          Понятно
        </button>
      </div>
    </div>
  );
}
